import { z } from "zod";
import { EntitySchema } from "./Entity";
import { CodeSchema } from "./Codes";

const ImportProjectTemplateSchema = z.object({
  templateId: z.string(),
  borrower: EntitySchema.nullable(),
  builder: EntitySchema.nullable(),
  inspector: EntitySchema.nullable(),
  projectId: z.string(),
  projectName: z.string(),
  projectAddress: z.string(),
  projectNotes: z.string(),
  projectInitLenderFunds: z.string(),
  projectAdjLenderFunds: z.string(),
  projectInitEquity: z.string(),
  projectAdjEquity: z.string(),
  budgetTab: z.number(),
  budgetStartRow: z.number(),
  budgetEndRow: z.number(),
  budgetLineRef: z.string(),
  budgetDescription: z.string(),
  budgetAmount: z.string(),
  budgetPriorDrawnToDate: z.string(),
  budgetThisDrawAmount: z.string(),
  budgetInspPriorDraw: z.string(),
  budgetInspThisDraw: z.string(),
  budgetDefaultCostType: z.string(),
  budgetPriorEquityDrawnToDate: z.string(),
  budgetEquityThisDrawAmount: z.string(),
  initialBudget: z.string(),
  templateName: z.string(),
});

export const ImportProjectSchema = z.object({
  drawNumber: z.number().default(0),
  selectedUserGroup: CodeSchema.nullish(),
  borrower: EntitySchema.nullable().default(null),
  builder: EntitySchema.nullable().default(null),
  inspector: EntitySchema.nullable().default(null),
  projectIdTab: z.number().default(0),
  projectIdCol: z.number().default(0),
  projectIdRow: z.number().default(0),
  projectNameTab: z.number().default(0),
  projectNameCol: z.number().default(0),
  projectNameRow: z.number().default(0),
  projectAddressTab: z.number().default(0),
  projectAddressCol: z.number().default(0),
  projectAddressRow: z.number().default(0),
  projectNotesTab: z.number().default(0),
  projectNotesCol: z.number().default(0),
  projectNotesRow: z.number().default(0),
  initialLenderFundsTab: z.number().default(0),
  initialLenderFundsCol: z.number().default(0),
  initialLenderFundsRow: z.number().default(0),
  adjustedLenderFundsTab: z.number().default(0),
  adjustedLenderFundsCol: z.number().default(0),
  adjustedLenderFundsRow: z.number().default(0),
  initialOtherEquityTab: z.number().default(0),
  initialOtherEquityCol: z.number().default(0),
  initialOtherEquityRow: z.number().default(0),
  adjustedOtherEquityTab: z.number().default(0),
  adjustedOtherEquityCol: z.number().default(0),
  adjustedOtherEquityRow: z.number().default(0),
});

export const ImportBudgetSchema = z.object({
  tab: z.number().default(1),
  startRow: z.number().default(1),
  endRow: z.number().default(10),
  lineRefCol: z.number().default(0),
  descriptionCol: z.number().default(0),
  budgetAmtCol: z.number().default(0),
  initBudgetAmtCol: z.number().default(0),
  priorDrawnToDateCol: z.number().default(0),
  thisDrawAmtCol: z.number().default(0),
  priorEquityDrawnToDateCol: z.number().default(0),
  thisEquityDrawAmtCol: z.number().default(0),
  inspPriorDrawCol: z.number().default(0),
  inspThisDrawCol: z.number().default(0),
  defaultCostType: z.string().default("INSP"),
});

export const ImportProjectFieldDataSchema = z.object({
  projectIdTab: z.number().default(1),
  projectIdCol: z.string().default(""),
  projectIdRow: z.number().default(1),
  projectNameTab: z.number().default(1),
  projectNameCol: z.string().default(""),
  projectNameRow: z.number().default(1),
  projectAddressTab: z.number().default(1),
  projectAddressCol: z.string().default(""),
  projectAddressRow: z.number().default(1),
  projectNotesTab: z.number().default(1),
  projectNotesCol: z.string().default(""),
  projectNotesRow: z.number().default(1),
  initialLenderFundsTab: z.number().default(1),
  initialLenderFundsCol: z.string().default(""),
  initialLenderFundsRow: z.number().default(1),
  adjustedLenderFundsTab: z.number().default(1),
  adjustedLenderFundsCol: z.string().default(""),
  adjustedLenderFundsRow: z.number().default(1),
  initialOtherEquityTab: z.number().default(1),
  initialOtherEquityCol: z.string().default(""),
  initialOtherEquityRow: z.number().default(1),
  adjustedOtherEquityTab: z.number().default(1),
  adjustedOtherEquityCol: z.string().default(""),
  adjustedOtherEquityRow: z.number().default(1),
});

export const ImportBudgetFieldDataSchema = z.object({
  lineRefCol: z.string().default(""),
  descriptionCol: z.string().default(""),
  budgetAmtCol: z.string().default(""),
  initBudgetAmtCol: z.string().default(""),
  priorDrawnToDateCol: z.string().default(""),
  thisDrawAmtCol: z.string().default(""),
  priorEquityDrawnToDateCol: z.string().default(""),
  thisEquityDrawAmtCol: z.string().default(""),
  inspPriorDrawCol: z.string().default(""),
  inspThisDrawCol: z.string().default(""),
});

export const ImportProjectTemplateListSchema = z.array(ImportProjectTemplateSchema);

export type ImportProjectTemplate = z.infer<typeof ImportProjectTemplateSchema>;
export type ImportProject = z.infer<typeof ImportProjectSchema>;
export type ImportBudget = z.infer<typeof ImportBudgetSchema>;
export type ImportProjectFieldData = z.infer<typeof ImportProjectFieldDataSchema>;
export type ImportBudgetFieldData = z.infer<typeof ImportBudgetFieldDataSchema>;
